import React from 'react';
import { Flex, Modal } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import CustomTitle from './CustomTitle';
import CustomParagraph from './CustomParagraph';

interface CustomModalWithoutButtonsProps {
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  modalTitle?: string;
  modalText?: string;
  modalSize?: string | number;
  children?: React.ReactNode;
  modalBorderRadius?: string;
  headerIcon?: React.ReactNode;
  headerExtra?: React.ReactNode;
  /** Renders below the title row when headerExtra is set. Use for e.g. a toggle row. */
  headerExtraBottom?: React.ReactNode;
  fontSize?: string;
  lineHeight?: string;
  modalTextFontSize?: string;
  closeIconSize?: string;
  className?: string;
  /** Stack above drawers / other overlays (e.g. nested UI). */
  zIndex?: number;
  /** Whether clicking the backdrop closes the modal. Defaults to true. */
  maskClosable?: boolean;
  /** Whether the close icon (X) is shown. Defaults to true. */
  closable?: boolean;
  /** Whether pressing Escape closes the modal. Defaults to true. */
  keyboard?: boolean;
  /** Body text color under the title (e.g. muted grey for request-style modals). */
  modalTextColor?: string;
}

const CustomModalWithoutButtons: React.FC<CustomModalWithoutButtonsProps> = ({
  modalOpen,
  setModalOpen,
  modalTitle,
  modalText,
  modalSize,
  children,
  modalBorderRadius = '8px',
  headerIcon,
  headerExtra,
  headerExtraBottom,
  fontSize = '28px',
  lineHeight = '36px',
  modalTextFontSize = '14px',
  closeIconSize = '15px',
  className,
  zIndex,
  maskClosable = true,
  closable,
  keyboard,
  modalTextColor,
}) => {
  const handleCancel = () => {
    setModalOpen(false);
  };

  const headerRow = headerExtra ? (
    <Flex vertical gap={headerExtraBottom ? 8 : 0} style={{ width: '100%' }}>
      <Flex
        align='center'
        justify='space-between'
        gap={8}
        style={{ width: '100%', minHeight: 22 }}
      >
        <CustomTitle
          className='ant-modal-heading'
          text={modalTitle}
          fontSize={fontSize}
          lineHeight={lineHeight}
          fontWeight='700'
          marginBottom='0'
        />
        <Flex align='center' gap={8}>
          {headerExtra}
          <button
            type='button'
            onClick={handleCancel}
            aria-label='Close'
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              margin: 0,
              border: 0,
              background: 'transparent',
              cursor: 'pointer',
              fontSize: closeIconSize,
              color: 'var(--text-primary)',
              lineHeight: 1,
            }}
          >
            <CloseOutlined
              style={{ fontSize: closeIconSize, color: 'var(--text-primary)' }}
            />
          </button>
        </Flex>
      </Flex>
      {headerExtraBottom && (
        <Flex align='center' justify='flex-end' style={{ width: '100%' }}>
          {headerExtraBottom}
        </Flex>
      )}
    </Flex>
  ) : null;

  return (
    <Modal
      title={headerRow ?? ''}
      centered
      open={modalOpen}
      onCancel={handleCancel}
      width={modalSize}
      className={className}
      zIndex={zIndex}
      footer={null}
      maskClosable={maskClosable}
      closable={closable ?? !headerExtra}
      keyboard={keyboard}
      closeIcon={
        <CloseOutlined
          style={{ fontSize: closeIconSize, color: 'var(--text-primary)' }}
        />
      }
      styles={{
        body: {
          borderRadius: modalBorderRadius,
        },
        ...(headerExtra && {
          header: {
            marginBottom: 0,
            paddingBottom: 0,
          },
        }),
      }}
    >
      {headerIcon && (
        <Flex justify='center' style={{ marginBottom: '16px' }}>
          {headerIcon}
        </Flex>
      )}

      <Flex vertical>
        {!headerExtra && (
          <CustomTitle
            className='ant-modal-heading'
            text={modalTitle}
            fontSize={fontSize}
            lineHeight={lineHeight}
            fontWeight='700'
            marginBottom='4px'
          />
        )}
        {modalText && (
          <CustomParagraph
            text={modalText}
            color={modalTextColor}
            fontSize={modalTextFontSize}
            fontWeight='400'
            lineHeight='20px'
            marginBottom='24px'
          />
        )}
      </Flex>

      <main>{children}</main>
    </Modal>
  );
};

export default CustomModalWithoutButtons;
